import * as Dialog from '@radix-ui/react-dialog'
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { CalendarDays, Plus, X, MapPin, Clock3, ArrowRight, Users, ChevronDown } from 'lucide-react'
import { useFamily } from '@/hooks'
import { Button } from '@/components/ui'
import { SectionHeader } from '@/components/hearth'
import {
  calendarApi,
  eventTime,
  googleCalendarUrl,
  outlookCalendarUrl,
  shiftCalendarDay,
  type FamilyEvent,
} from '@/lib/api/calendar'

const field =
  'mt-1.5 w-full min-w-0 rounded-xl border border-gray-300 bg-surface px-3 py-2.5 text-base font-normal text-gray-900 shadow-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50'
const label = 'block min-w-0 text-sm font-medium text-gray-700'
const deviceZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone
function message(error: unknown): string {
  const e = error as { message?: string; details?: { message: string }[] }
  return (
    e?.details?.map((d) => d.message).join(' ') ||
    e?.message ||
    'Something went wrong. Please try again.'
  )
}
function useCalendarNow() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}
function wallTime(value: string | null, zone: string): string {
  if (!value) return ''
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(value))
  return parts.replace(' ', 'T')
}

function EventEditor({
  family,
  event,
  emailAvailable,
  onClose,
  onSaved,
}: {
  family: string
  event?: FamilyEvent
  emailAvailable: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = useState(event?.title || '')
  const [zone, setZone] = useState(event?.time_zone || deviceZone())
  const [allDay, setAllDay] = useState(event?.all_day || false)
  const [start, setStart] = useState(
    event?.all_day ? event.start_date! : wallTime(event?.start_at || new Date().toISOString(), zone)
  )
  const [end, setEnd] = useState(
    event?.all_day
      ? shiftCalendarDay(event.end_date!, -1)
      : wallTime(event?.end_at || new Date(Date.now() + 3600000).toISOString(), zone)
  )
  const [location, setLocation] = useState(event?.location || '')
  const [notes, setNotes] = useState(event?.notes || '')
  const [inviteFamily, setInviteFamily] = useState(false)
  const [emails, setEmails] = useState('')
  const [startFold, setStartFold] = useState('')
  const [endFold, setEndFold] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  async function save(e: React.FormEvent) {
    e.preventDefault()
    // Timed validation belongs to the server: repeated DST hours can run backwards on the clock.
    if (allDay && end < start) {
      setError('The event must end after it starts. Check the dates and times.')
      return
    }
    setBusy(true)
    setError('')
    try {
      await calendarApi.save(
        family,
        {
          title,
          location,
          notes,
          time_zone: zone,
          all_day: allDay,
          ...(allDay
            ? { start_date: start, end_date: shiftCalendarDay(end, 1) }
            : {
                start_at:
                  event && zone === event.time_zone && start === wallTime(event.start_at, zone)
                    ? event.start_at
                    : start,
                end_at:
                  event && zone === event.time_zone && end === wallTime(event.end_at, zone)
                    ? event.end_at
                    : end,
                ...(startFold ? { start_fold: Number(startFold) } : {}),
                ...(endFold ? { end_fold: Number(endFold) } : {}),
              }),
          version: event?.version,
          invite_family: inviteFamily,
          invite_emails: emails
            .split(/[,;\n]/)
            .map((s) => s.trim())
            .filter(Boolean),
        },
        event?.id
      )
      onSaved()
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm" />
        <Dialog.Content
          onEscapeKeyDown={(e) => {
            if (busy) e.preventDefault()
          }}
          onPointerDownOutside={(e) => e.preventDefault()}
          className="bg-surface fixed top-1/2 left-1/2 z-[101] flex max-h-[calc(100dvh-2rem)] w-[calc(100%_-_2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-gray-200 text-gray-900 shadow-2xl"
        >
          <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5">
            <div>
              <Dialog.Title className="font-serif text-2xl font-semibold">
                {event ? 'Edit event' : 'Add a family event'}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-gray-500">
                Appointments and plans everyone can keep up with.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={busy}
                aria-label="Close event editor"
              >
                <X className="h-5 w-5" />
              </Button>
            </Dialog.Close>
          </div>
          <form onSubmit={save} className="flex min-h-0 flex-col">
            <fieldset disabled={busy} className="min-h-0 space-y-5 overflow-y-auto px-6 py-5">
              <label className={label}>
                Event title
                <input
                  autoFocus
                  required
                  maxLength={200}
                  className={field}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Mom’s annual checkup"
                />
              </label>
              <div className="bg-canvas space-y-4 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    <Clock3 className="text-primary-600 h-4 w-4" />
                    Date & time
                  </span>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      className="accent-primary-600 h-4 w-4"
                      type="checkbox"
                      checked={allDay}
                      onChange={(e) => {
                        setAllDay(e.target.checked)
                        setStart(start.slice(0, 10) + (e.target.checked ? '' : 'T09:00'))
                        setEnd(end.slice(0, 10) + (e.target.checked ? '' : 'T10:00'))
                      }}
                    />
                    All day
                  </label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={label}>
                    Starts
                    <input
                      required
                      className={field}
                      type={allDay ? 'date' : 'datetime-local'}
                      value={start}
                      onChange={(e) => setStart(e.target.value)}
                    />
                  </label>
                  <label className={label}>
                    {allDay ? 'Last day' : 'Ends'}
                    <input
                      required
                      min={allDay ? start : undefined}
                      className={field}
                      type={allDay ? 'date' : 'datetime-local'}
                      value={end}
                      onChange={(e) => setEnd(e.target.value)}
                    />
                  </label>
                </div>
                <label className={label}>
                  Appointment timezone
                  <input
                    required
                    className={field}
                    value={zone}
                    onChange={(e) => setZone(e.target.value)}
                    list="calendar-timezones"
                  />
                  <datalist id="calendar-timezones">
                    {Array.from(
                      new Set([deviceZone(), ...Intl.supportedValuesOf('timeZone'), 'UTC'])
                    ).map((z) => (
                      <option key={z} value={z} />
                    ))}
                  </datalist>
                </label>
                <p className="text-xs leading-relaxed text-gray-500">
                  Choose the timezone where the appointment takes place. Everyone sees the time in
                  their own timezone.
                </p>
              </div>
              <label className={label}>
                Location (optional)
                <input
                  className={field}
                  value={location}
                  maxLength={500}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Clinic name, address, or video link"
                />
              </label>
              <details className="group rounded-xl border border-gray-200 p-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-semibold">
                  Notes and email invitations
                  <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
                </summary>
                <div className="mt-4 space-y-4">
                  <label className={label}>
                    Notes
                    <textarea
                      className={field}
                      rows={3}
                      value={notes}
                      maxLength={10000}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything the family should know or bring"
                    />
                  </label>
                  <label className="flex items-start gap-3 text-sm">
                    <input
                      className="accent-primary-600 mt-1 h-4 w-4 shrink-0"
                      type="checkbox"
                      checked={inviteFamily}
                      disabled={!emailAvailable}
                      onChange={(e) => setInviteFamily(e.target.checked)}
                    />
                    Email a calendar invitation to everyone in the family
                  </label>
                  <label className={label}>
                    Invite by email (separate addresses with commas)
                    <textarea
                      className={field}
                      rows={2}
                      value={emails}
                      onChange={(e) => setEmails(e.target.value)}
                      disabled={!emailAvailable}
                      placeholder="name@example.com"
                    />
                  </label>
                  <p className="text-xs leading-relaxed text-gray-500">
                    {emailAvailable
                      ? 'Invitees receive event details, updates and cancellations. Existing invitees stay included. Email replies are not tracked here.'
                      : 'Email invitations are not available yet. After saving, you can download a calendar file or add the event to your calendar.'}
                  </p>
                </div>
              </details>
              {error && (
                <div role="alert" className="bg-error-50 text-error-700 rounded-xl p-4 text-sm">
                  {error}
                </div>
              )}
              {error && /ambiguous|occurs twice|fold|earlier occurrence/i.test(error) && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className={label}>
                    If the start time occurs twice
                    <select
                      className={field}
                      value={startFold}
                      onChange={(e) => setStartFold(e.target.value)}
                    >
                      <option value="">Choose if requested</option>
                      <option value="0">Earlier occurrence</option>
                      <option value="1">Later occurrence</option>
                    </select>
                  </label>
                  <label className={label}>
                    If the end time occurs twice
                    <select
                      className={field}
                      value={endFold}
                      onChange={(e) => setEndFold(e.target.value)}
                    >
                      <option value="">Choose if requested</option>
                      <option value="0">Earlier occurrence</option>
                      <option value="1">Later occurrence</option>
                    </select>
                  </label>
                </div>
              )}
            </fieldset>
            <div className="bg-surface flex shrink-0 items-center justify-between gap-4 border-t border-gray-200 px-6 py-4">
              <span className="hidden items-center gap-2 text-xs text-gray-500 sm:flex">
                <Users className="h-4 w-4" />
                Visible to your family
              </span>
              <div className="ml-auto flex gap-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                  Close
                </Button>
                <Button disabled={busy}>{busy ? 'Saving…' : 'Save event'}</Button>
              </div>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export function CalendarPage() {
  const { selectedFamilyId, families, setSelectedFamilyId } = useFamily()
  const [params] = useSearchParams()
  const requested = params.get('family_id')
  useEffect(() => {
    if (requested && requested !== selectedFamilyId && families.some((f) => f.id === requested))
      setSelectedFamilyId(requested)
  }, [requested, selectedFamilyId, families, setSelectedFamilyId])
  return selectedFamilyId ? (
    <FamilyCalendar key={selectedFamilyId} family={selectedFamilyId} />
  ) : (
    <p>Select a family to see its calendar.</p>
  )
}

function FamilyCalendar({ family }: { family: string }) {
  const now = useCalendarNow()
  const cache = useQueryClient()
  const agenda = useQuery({
    queryKey: ['calendar', family],
    queryFn: () => calendarApi.list(family),
    refetchInterval: 60000,
  })
  const [editor, setEditor] = useState<FamilyEvent | 'new' | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [past, setPast] = useState(false)
  const refresh = () => {
    void cache.invalidateQueries({ queryKey: ['calendar', family] })
  }
  async function run(work: () => Promise<unknown>) {
    setBusy(true)
    setError('')
    try {
      await work()
      refresh()
    } catch (err) {
      setError(message(err))
    } finally {
      setBusy(false)
    }
  }
  const events =
    agenda.data?.events.filter((e) => (past ? !isUpcoming(e, now) : isUpcoming(e, now))) || []
  events.sort((a, b) =>
    (a.all_day ? a.start_date! : wallTime(a.start_at, deviceZone())).localeCompare(
      b.all_day ? b.start_date! : wallTime(b.start_at, deviceZone())
    )
  )
  if (past) events.reverse()
  async function download(event: FamilyEvent) {
    const blob = await calendarApi.ics(family, event.id)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'family-event.ics'
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  return (
    <div className="mx-auto max-w-4xl space-y-8 text-gray-900">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-primary-600 text-xs font-semibold tracking-wider uppercase">
            A little planning, together
          </p>
          <h1 className="mt-2 font-serif text-3xl font-semibold sm:text-4xl">Family calendar</h1>
          <p className="mt-2 text-sm text-gray-500">
            Appointments and everyday plans, all in one shared place.
          </p>
        </div>
        {agenda.data?.can_create && (
          <Button className="self-start sm:self-auto" onClick={() => setEditor('new')}>
            <Plus className="mr-2 h-4 w-4" />
            Add event
          </Button>
        )}
      </header>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <div
          className="inline-flex gap-1 rounded-xl bg-gray-100 p-1"
          role="group"
          aria-label="Events to show"
        >
          {[
            ['Upcoming', false],
            ['Past & cancelled', true],
          ].map(([title, value]) => (
            <button
              type="button"
              key={String(title)}
              aria-pressed={past === value}
              onClick={() => setPast(Boolean(value))}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${past === value ? 'bg-surface text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              {title}
            </button>
          ))}
        </div>
        <span className="flex items-center gap-2 text-xs text-gray-500">
          <Clock3 className="h-4 w-4" />
          Times shown in {deviceZone().replaceAll('_', ' ')}
        </span>
      </div>
      {(error || agenda.error) && (
        <div
          role="alert"
          className="bg-error-50 text-error-700 flex flex-wrap items-center justify-between gap-3 rounded-xl p-4 text-sm"
        >
          {error || message(agenda.error)}
          {agenda.error && (
            <Button variant="outline" onClick={() => void agenda.refetch()}>
              Try again
            </Button>
          )}
        </div>
      )}
      {agenda.isLoading && (
        <div role="status" className="hearth-card p-8 text-center text-gray-500">
          Loading calendar…
        </div>
      )}
      {!agenda.isLoading && !agenda.error && events.length === 0 && (
        <div className="hearth-card px-6 py-14 text-center">
          <span className="bg-primary-50 text-primary-600 mx-auto flex h-14 w-14 items-center justify-center rounded-2xl">
            <CalendarDays className="h-7 w-7" />
          </span>
          <h2 className="mt-5 font-serif text-2xl font-semibold">
            {past ? 'No past events yet' : 'Make room for what matters'}
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500">
            {past
              ? 'Completed and cancelled plans will appear here.'
              : agenda.data?.can_create
                ? 'No upcoming events. Add an appointment or ask a family member to share one.'
                : 'No upcoming family events.'}
          </p>
        </div>
      )}
      <fieldset disabled={busy} className="space-y-5">
        {events.map((event) => (
          <article key={event.id} className="hearth-card overflow-hidden">
            <div className="flex gap-4 p-5 sm:gap-6 sm:p-6">
              <EventDate event={event} />
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-serif text-xl font-semibold break-words sm:text-2xl">
                      {event.title}
                    </h2>
                    {event.cancelled && (
                      <span className="mt-1 inline-block rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600">
                        Cancelled
                      </span>
                    )}
                  </div>
                  {event.can_edit && !event.cancelled && (
                    <Button variant="ghost" size="sm" onClick={() => setEditor(event)}>
                      Edit
                    </Button>
                  )}
                </div>
                <p className="flex items-start gap-2 text-sm leading-relaxed text-gray-600">
                  <Clock3 className="text-primary-600 mt-0.5 h-4 w-4 shrink-0" />
                  <span>{eventTime(event)}</span>
                </p>
                {event.location && (
                  <p className="flex items-start gap-2 text-sm text-gray-600">
                    <MapPin className="text-primary-600 mt-0.5 h-4 w-4 shrink-0" />
                    <span className="break-words">{event.location}</span>
                  </p>
                )}
                {event.notes && (
                  <p className="border-primary-100 border-l-2 pl-3 text-sm leading-relaxed break-words whitespace-pre-wrap text-gray-500">
                    {event.notes}
                  </p>
                )}
                {event.source !== 'app' && (
                  <p className="text-sm text-gray-500">
                    From {event.source}.{' '}
                    {/^https:\/\//.test(event.source_url) && (
                      <a
                        className="underline"
                        href={event.source_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open original to edit
                      </a>
                    )}
                  </p>
                )}
                {!event.cancelled && !past && (
                  <div className="space-y-3 pt-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="mr-1 text-xs font-medium text-gray-500">
                        Will you be there?
                      </span>
                      {[
                        ['yes', 'Going'],
                        ['maybe', 'Maybe'],
                        ['no', 'Can’t attend'],
                      ].map(([value, title]) => (
                        <Button
                          key={value}
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            run(() =>
                              calendarApi.action(family, event.id, 'rsvp', { response: value })
                            )
                          }
                        >
                          {title}
                        </Button>
                      ))}
                    </div>
                    {!!event.responses.length && (
                      <p className="text-xs text-gray-500">
                        {event.responses
                          .map(
                            (r) =>
                              `${r.name}: ${r.response === 'yes' ? 'Going' : r.response === 'no' ? 'Declined' : 'Maybe'}`
                          )
                          .join(' · ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <details className="group bg-canvas border-t border-gray-200 px-5 py-4 sm:px-6">
              <summary className="text-primary-700 flex cursor-pointer list-none items-center justify-between text-sm font-medium">
                Add to your calendar & invitations
                <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-4 space-y-4">
                <p className="text-xs leading-relaxed text-gray-500">
                  Calendar links and files save a one-time copy. Email invitees also receive changes
                  and cancellations.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => run(() => download(event))}>
                    Download calendar file
                  </Button>
                  {!event.cancelled && (
                    <>
                      {[
                        [googleCalendarUrl(event), 'Google Calendar'],
                        [outlookCalendarUrl(event), 'Outlook'],
                        [outlookCalendarUrl(event, true), 'Outlook work/school'],
                      ].map(([url, title]) => (
                        <Button key={title} asChild size="sm" variant="outline">
                          <a href={url} target="_blank" rel="noreferrer">
                            {title}
                          </a>
                        </Button>
                      ))}
                    </>
                  )}
                </div>
                {event.invitations.map((i) => (
                  <p key={i.email} className="text-xs break-words text-gray-600">
                    {i.email}: {i.error || i.status}
                  </p>
                ))}
                {event.can_edit && !!event.invitations.length && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => run(() => calendarApi.action(family, event.id, 'retry'))}
                  >
                    Retry pending invitations
                  </Button>
                )}
                {event.can_edit && !event.cancelled && (
                  <div className="border-t border-gray-200 pt-3">
                    <Button
                      size="sm"
                      variant="destructiveSoft"
                      onClick={() => {
                        if (window.confirm('Cancel this event and notify its email invitees?'))
                          run(() =>
                            calendarApi.action(family, event.id, 'cancel', {
                              version: event.version,
                            })
                          )
                      }}
                    >
                      Cancel event
                    </Button>
                  </div>
                )}
              </div>
            </details>
          </article>
        ))}
      </fieldset>
      {agenda.data?.family_email && (
        <p className="flex items-start gap-2 text-xs break-all text-gray-500">
          <Users className="h-4 w-4 shrink-0" />
          Invitations come from {agenda.data.family_email}.
        </p>
      )}
      {editor && (
        <EventEditor
          family={family}
          event={editor === 'new' ? undefined : editor}
          emailAvailable={!!agenda.data?.email_available}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null)
            refresh()
          }}
        />
      )}
    </div>
  )
}

export function UpcomingFamilyEvents() {
  const now = useCalendarNow()
  const { selectedFamilyId } = useFamily()
  const query = useQuery({
    queryKey: ['calendar', selectedFamilyId],
    queryFn: () => calendarApi.list(selectedFamilyId!),
    enabled: !!selectedFamilyId,
    staleTime: 60000,
  })
  const events =
    query.data?.events
      .filter(
        (e) =>
          !e.cancelled &&
          (e.all_day
            ? e.end_date! > now.toLocaleDateString('sv-SE')
            : new Date(e.end_at!).getTime() >= now.getTime())
      )
      .sort((a, b) =>
        (a.all_day ? a.start_date! : wallTime(a.start_at, deviceZone())).localeCompare(
          b.all_day ? b.start_date! : wallTime(b.start_at, deviceZone())
        )
      )
      .slice(0, 3) || []
  if (!selectedFamilyId) return null
  return (
    <section className="space-y-3">
      <SectionHeader title="Upcoming events & appointments" />
      <Link
        to="/calendar"
        className="hearth-card hearth-press focus-visible:ring-primary-500 block p-5 focus-visible:ring-2 focus-visible:outline-none"
      >
        <div className="space-y-4">
          {query.error ? (
            <p className="text-sm text-gray-500">Calendar could not be loaded. Open it to retry.</p>
          ) : events.length ? (
            events.map((event) => (
              <div className="flex items-start gap-4" key={event.id}>
                <EventDate event={event} />
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900">{event.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-500">{eventTime(event)}</p>
                  {event.location && <p className="mt-1 text-xs text-gray-500">{event.location}</p>}
                </div>
              </div>
            ))
          ) : (
            <div className="flex items-start gap-3">
              <CalendarDays className="text-primary-600 h-6 w-6 shrink-0" />
              <div>
                <p className="font-medium text-gray-900">
                  {query.isLoading ? 'Loading upcoming events…' : 'No upcoming plans'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {query.data?.can_create
                    ? 'Add an appointment or family event so everyone knows what’s next.'
                    : 'Family appointments will appear here.'}
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="text-primary-600 mt-4 flex items-center justify-between border-t border-gray-100 pt-4 text-sm font-medium">
          View family calendar
          <ArrowRight className="h-4 w-4" />
        </div>
      </Link>
    </section>
  )
}

function isUpcoming(event: FamilyEvent, now: Date) {
  return (
    !event.cancelled &&
    (event.all_day
      ? event.end_date! > now.toLocaleDateString('sv-SE')
      : new Date(event.end_at!).getTime() >= now.getTime())
  )
}

function EventDate({ event }: { event: FamilyEvent }) {
  const date = new Date(event.all_day ? `${event.start_date}T12:00:00` : event.start_at!)
  return (
    <div
      aria-hidden="true"
      className="bg-primary-50 text-primary-700 flex w-14 shrink-0 flex-col items-center self-start rounded-xl px-2 py-2"
    >
      <span className="text-[10px] font-semibold tracking-wider uppercase">
        {date.toLocaleDateString(undefined, { month: 'short' })}
      </span>
      <span className="font-serif text-2xl leading-tight font-semibold">{date.getDate()}</span>
      <span className="text-[10px]">
        {date.toLocaleDateString(undefined, { weekday: 'short' })}
      </span>
    </div>
  )
}
